import { NextSeo } from 'next-seo';
import React from 'react';
import Container from '../components/Container';
import PageHeader from '../components/PageHeader';
import config from '../config';

function TermsAndConditionsPage() {
  const description = `Review the Terms & Conditions for using Shitty Kitties and SK Club.`;

  return (
    <>
      <NextSeo title={`Terms & Conditions - ${config.siteName}`} description={description} />

      <div className='pb-12 md:pb-48 bg-bg'>
        <PageHeader heading={'Terms & Conditions'} description={description} />
        <Container className='mx-auto'>
          <p className='text-sm text-gray-400 mb-6'>Effective Date: April 10, 2026</p>

          <div className='prose'>
            <h2>Introduction</h2>
            <p>
              Welcome to Shitty Kitties and SK Club ("we," "our," "us"). By accessing or using{' '}
              <a href='https://www.shittykitties.art'>https://www.shittykitties.art</a> (the "Site") or our SK Club mobile applications, including
              Frik'd Up Fishies (the "Apps"), collectively referred to as our "Services," you agree to comply with and be bound by the following Terms
              and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Services.
            </p>

            <h2>1. Use of Services</h2>
            <p>
              You agree to use our Services in compliance with all applicable laws and regulations. You are responsible for maintaining the
              confidentiality of any login credentials and for all activities that occur under your account.
            </p>

            <h2>2. Mobile Application Usage</h2>
            <p>
              <strong>App Store Compliance:</strong> Your use of our Apps is also subject to the terms and conditions of the applicable app store
              (Apple App Store or Google Play Store). In the event of a conflict between these Terms and the app store terms, the app store terms
              shall govern with respect to your use of the Apps.
            </p>
            <p>
              <strong>Device Compatibility:</strong> Our Apps require compatible devices and operating systems. We do not guarantee that our Apps will
              be compatible with all devices or operating system versions.
            </p>
            <p>
              <strong>Availability:</strong> We do not guarantee continuous, uninterrupted availability of our Apps. The Apps may be temporarily
              unavailable due to maintenance, updates, server issues, or circumstances beyond our control.
            </p>
            <p>
              <strong>Updates:</strong> We may release updates to our Apps from time to time, including over-the-air updates via Expo. Some updates
              may be required for continued use of the Apps.
            </p>

            <h2>3. Intellectual Property Rights</h2>
            <p>
              All content across our Services, including but not limited to text, graphics, logos, images, NFT artwork, fish artwork, animations,
              game mechanics, sound effects, music, and software code, are the property of Shitty Kitties / SK Club or its content creators and are
              protected by copyright, trademark, and other intellectual property laws. You may not use any of our intellectual property without prior
              written permission.
            </p>

            <h2>4. User-Generated Content</h2>
            <p>
              Users may have opportunities to interact with our Services, such as posting comments or sharing content. By submitting any content, you
              grant Shitty Kitties / SK Club a non-exclusive, royalty-free, worldwide, sublicensable, and transferable license to use, reproduce,
              display, and distribute your content as necessary to operate our Services.
            </p>

            <h2>5. Virtual Currency and In-App Purchases</h2>
            <p>
              <strong>Virtual Currency:</strong> Our Apps may include virtual currencies (such as coins) that can be earned through gameplay or
              purchased. Virtual currencies have no real-world monetary value, cannot be exchanged for real currency, and are not redeemable for cash.
              Virtual currency balances are not transferable between users.
            </p>
            <p>
              <strong>In-App Purchases:</strong> Purchases made within our Apps are processed through the Apple App Store or Google Play Store and
              are subject to their respective terms and refund policies. All purchases are final unless otherwise required by applicable law or the
              policies of the relevant app store.
            </p>
            <p>
              <strong>Subscriptions:</strong> Some features may be offered through subscription plans managed by RevenueCat and processed through the
              Apple App Store or Google Play Store. Subscription terms, including pricing, billing frequency, and cancellation, are governed by the
              applicable app store's policies. You can manage or cancel subscriptions through your device's app store settings.
            </p>

            <h2>6. User Game Data</h2>
            <p>
              Game progress, preferences, and related data are stored locally on your device and may optionally be synced to the cloud via Firebase.
              We are not responsible for data loss resulting from device issues, app deletion, operating system changes, or other circumstances beyond
              our control. We recommend enabling cloud sync for backup purposes, but we do not guarantee the availability or integrity of cloud-stored
              data.
            </p>

            <h2>7. Prohibited Conduct</h2>
            <p>When using our Services, you agree not to:</p>
            <ul>
              <li>Engage in illegal activities or use our Services for unlawful purposes;</li>
              <li>Upload or transmit any malicious code, viruses, or harmful content;</li>
              <li>Interfere with or disrupt the operations or security of our Services;</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts;</li>
              <li>
                Use cheats, exploits, automation software, bots, or any unauthorized third-party tools to modify or interfere with our Apps;
              </li>
              <li>Reverse engineer, decompile, or disassemble any part of our Apps.</li>
            </ul>

            <h2>8. NFTs and Digital Assets (Website Only)</h2>
            <p>
              The following applies to the Shitty Kitties website (<a href='https://www.shittykitties.art'>shittykitties.art</a>) and does not apply
              to our SK Club mobile Apps. Shitty Kitties offers digital art and NFTs on the Algorand blockchain. By purchasing or interacting with
              NFTs, you acknowledge that you understand the risks involved with digital assets and blockchain technology. Shitty Kitties makes no
              guarantees regarding the future value of NFTs, and you are solely responsible for securing and managing your digital assets.
            </p>

            <h2>9. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Shitty Kitties / SK Club will not be liable for any indirect, incidental, special,
              consequential, or punitive damages, including loss of data, revenue, profits, virtual currency, or game progress, arising out of or
              related to your use of our Services.
            </p>
            <p>
              <strong>Physical Harm and Device Damage:</strong> Our Apps may include interactive gameplay mechanics such as tapping, swiping, or other
              repetitive motions. You acknowledge and agree that Shitty Kitties / SK Club is not responsible for any bodily harm, repetitive strain
              injuries, discomfort, or physical damage to your device that may result from extended or vigorous use of our Apps. You use our Apps at
              your own risk and are solely responsible for taking appropriate breaks and using our Apps in a safe manner.
            </p>

            <h2>10. Disclaimers</h2>
            <p>
              Our Services and all content are provided "as-is" without any warranties of any kind, either express or implied. Shitty Kitties / SK
              Club disclaims all warranties, including but not limited to warranties of merchantability, fitness for a particular purpose, and
              non-infringement.
            </p>

            <h2>11. Modifications to Terms</h2>
            <p>
              We may update these Terms periodically to reflect changes in our practices or legal requirements. When we make changes, we will update
              the "Effective Date" and notify users as appropriate. Your continued use of our Services after such changes constitutes your acceptance
              of the revised Terms.
            </p>

            <h2>12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the United States, without regard to its conflict of law principles. Any disputes arising under
              these Terms shall be resolved exclusively in the courts of the United States.
            </p>

            <h2>13. Contact Us</h2>
            <p>If you have any questions or concerns regarding these Terms, please contact us at:</p>
            <p>
              <strong>Email:</strong> <a href='mailto:support@minnerlabs.com'>support@minnerlabs.com</a>
            </p>

            <p>
              By using our Services, including <a href='https://www.shittykitties.art'>https://www.shittykitties.art</a> and our SK Club mobile
              applications, you acknowledge that you have read, understood, and agree to these Terms & Conditions.
            </p>
          </div>
        </Container>
      </div>
    </>
  );
}

export default TermsAndConditionsPage;
